import React, { useState, useMemo } from 'react';

const DataTable = ({
  columns = [],
  data = [],
  defaultRowsPerPage = 10,
  tableId = 'dataTable',
  // Server-side pagination: when provided, page/rowsPerPage are controlled and data is current page only
  totalCount,
  page: controlledPage,
  rowsPerPage: controlledRowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  className = '',
  isLoading = true,
}) => {
  const isServerSide = totalCount !== undefined && totalCount !== null;

  const [internalPage, setInternalPage] = useState(1);
  const [internalRowsPerPage, setInternalRowsPerPage] = useState(defaultRowsPerPage);
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const page = isServerSide ? controlledPage : internalPage;
  const rowsPerPage = isServerSide ? controlledRowsPerPage : internalRowsPerPage;

  const sortableColumns = useMemo(
    () => columns.filter((col) => col.sortable !== false && col.key),
    [columns]
  );

  const sortedData = useMemo(() => {
    if (!sortBy || !data.length) return data;
    const key = sortBy;

    const parseDate = (value) => {
      if (value instanceof Date) return value.getTime();
      if (typeof value === 'string') {
        const ts = Date.parse(value);
        return Number.isNaN(ts) ? null : ts;
      }
      return null;
    };

    const parseNumber = (value) => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const num = Number(trimmed);
        return Number.isNaN(num) ? null : num;
      }
      return null;
    };

    return [...data].sort((a, b) => {
      const aRaw = a[key];
      const bRaw = b[key];

      if (aRaw == null && bRaw == null) return 0;
      if (aRaw == null) return sortDirection === 'asc' ? 1 : -1;
      if (bRaw == null) return sortDirection === 'asc' ? -1 : 1;

      // Date comparison (Date objects or date-like strings)
      const aDate = parseDate(aRaw);
      const bDate = parseDate(bRaw);
      if (aDate != null && bDate != null) {
        const diff = aDate - bDate;
        return sortDirection === 'asc' ? diff : -diff;
      }

      // Numeric comparison (numbers or numeric strings)
      const aNum = parseNumber(aRaw);
      const bNum = parseNumber(bRaw);
      if (aNum != null && bNum != null) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      // Fallback to string comparison
      const aStr = String(aRaw);
      const bStr = String(bRaw);
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [data, sortBy, sortDirection]);

  const totalPages = isServerSide
    ? Math.max(1, Math.ceil(totalCount / rowsPerPage))
    : Math.max(1, Math.ceil(sortedData.length / rowsPerPage));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const paginatedData = useMemo(() => {
    if (isServerSide) return sortedData;
    const start = (currentPage - 1) * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, currentPage, rowsPerPage, isServerSide]);

  const handleSort = (key) => {
    // if (isServerSide) return; // Server-side sort could be added later via onSortChange
    if (sortBy === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (newPage) => {
    if (isServerSide && onPageChange) {
      onPageChange(newPage);
    } else {
      setInternalPage(newPage);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const val = parseInt(e.target.value, 10);
    if (Number.isNaN(val) || val < 1 || val > 100) return;
    if (isServerSide && onRowsPerPageChange) {
      onRowsPerPageChange(val);
      onPageChange && onPageChange(1);
    } else {
      setInternalRowsPerPage(val);
      setInternalPage(1);
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const pages = [1];
    if (currentPage > 3) pages.push('...');
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) {
      if (!pages.includes(i)) pages.push(i);
    }
    if (currentPage < totalPages - 2) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  return (
    <table className={`lm-grid-table ${className}`} id={tableId}>
      <thead>
        <tr>
          {columns.map((col) => (
            <th key={col.key || col.label}>
              {col.sortable !== false && col.key ? (
                <div
                  className="d-flex"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleSort(col.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSort(col.key);
                    }
                  }}
                >
                  {col.label}
                  <span className="sort-arrow">
                    <div
                      className={`lm-angle-up-icon ${sortBy === col.key && sortDirection === 'asc'
                        ? 'active'
                        : ''
                        }`}
                    />
                    <div
                      className={`lm-angle-down-icon ${sortBy === col.key && sortDirection === 'desc'
                        ? 'active'
                        : ''
                        }`}
                    />
                  </span>
                </div>
              ) : (
                col.label
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {
          isLoading ? (
            <tr>
              <td colSpan={columns.length} className="text-center">
                Loading...
              </td>
            </tr>
          )
            : paginatedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center">
                  No data to display
                </td>
              </tr>
            ) : (
              paginatedData.map((row, rowIndex) => (
                <tr key={row.id ?? row.Id ?? rowIndex}>
                  {columns.map((col) => (
                    <td key={col.key || col.label} className={col.cellClassName || ''}>
                      {typeof col.render === 'function'
                        ? col.render(row[col.key], row, rowIndex)
                        : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            )}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={columns.length}>
            <div className="pagination">
              <div className="d-flex">
                <div
                  className="lm-angle-left-icon"
                  role="button"
                  tabIndex={0}
                  onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && currentPage > 1) {
                      e.preventDefault();
                      handlePageChange(currentPage - 1);
                    }
                  }}
                  aria-label="Previous page"
                />
                <div id="paginationButtons">
                  {getPageNumbers().map((p, i) =>
                    p === '...' ? (
                      <div key={`ellipsis-${i}`}>...</div>
                    ) : (
                      <div
                        key={p}
                        role="button"
                        tabIndex={0}
                        className={currentPage === p ? 'active' : ''}
                        onClick={() => handlePageChange(p)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePageChange(p);
                          }
                        }}
                      >
                        {p}
                      </div>
                    )
                  )}
                </div>
                <div
                  className="lm-angle-right-icon"
                  role="button"
                  tabIndex={0}
                  onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && currentPage < totalPages) {
                      e.preventDefault();
                      handlePageChange(currentPage + 1);
                    }
                  }}
                  aria-label="Next page"
                />
              </div>
              <div>
                <input
                  value={rowsPerPage}
                  className="text-center page-number-box"
                  type="number"
                  id="rowsPerPage"
                  min={1}
                  max={100}
                  onChange={handleRowsPerPageChange}
                />
                <span className="lm-text-light ml-2">Rows per page</span>
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
    </table>
  );
};

export default DataTable;
